import React from "react";
import type { OnboardingStatus } from "../../types/telemetry.ts";

interface WebsiteBadgeProps {
  status: OnboardingStatus | string;
  className?: string;
}

export const WebsiteBadge: React.FC<WebsiteBadgeProps> = ({ status, className = "" }) => {
  const normalized = status.toUpperCase();

  switch (normalized) {
    case "ACTIVE":
      return (
        <span
          data-testid="website-status-active"
          className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-mono bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 ${className}`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
          <span>ACTIVE · Protected</span>
        </span>
      );

    case "DRAFT":
      return (
        <span
          data-testid="website-status-draft"
          className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-mono bg-amber-500/10 text-amber-400 border border-amber-500/30 ${className}`}
          title="Draft website: visitor traffic is not yet routed and protection is inactive."
        >
          <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
          <span>DRAFT · Inactive</span>
        </span>
      );

    case "PENDING_OWNERSHIP":
      return (
        <span
          data-testid="website-status-pending"
          className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-mono bg-blue-500/10 text-blue-400 border border-blue-500/30 ${className}`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
          <span>Pending Verification</span>
        </span>
      );

    case "OWNERSHIP_VERIFIED":
      return (
        <span
          data-testid="website-status-verified"
          className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-mono bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 ${className}`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
          <span>Ownership Verified</span>
        </span>
      );

    case "ROUTING_READY":
      return (
        <span
          data-testid="website-status-routing-ready"
          className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-mono bg-indigo-500/10 text-indigo-400 border border-indigo-500/30 ${className}`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
          <span>Routing Ready</span>
        </span>
      );

    case "PROVISIONING_TLS":
      return (
        <span
          data-testid="website-status-provisioning-tls"
          className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-mono bg-purple-500/10 text-purple-400 border border-purple-500/30 ${className}`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-pulse" />
          <span>Provisioning TLS</span>
        </span>
      );

    case "DEACTIVATED":
    case "SUSPENDED":
      return (
        <span
          data-testid="website-status-deactivated"
          className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-mono bg-rose-500/10 text-rose-400 border border-rose-500/30 ${className}`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-rose-400" />
          <span>{normalized}</span>
        </span>
      );

    default:
      return (
        <span
          data-testid="website-status-other"
          className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-mono bg-panel-subtle text-foreground-muted border border-border ${className}`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-foreground-muted" />
          <span>{status}</span>
        </span>
      );
  }
};
